import { useBootstrapApi } from '@aibees/elephant/hooks/bootstrap/use-bootstrap-api';
import { useCallback, useMemo } from 'react';
import { TransformedData } from '../types';
import {
  getComingSoonFieldKeys,
  getValidExportFieldKeys,
  stripUnexportableFields,
} from '../utils/strip-unexportable-fields';
import { useExportOptions } from './use-export-options';

/**
 * Returns a stable function that removes fields which must not reach the export
 * backend — columns no longer present in bootstrap, and coming-soon columns —
 * from a built `setting` payload. Centralised here so the export and enrichment
 * dialogs share one implementation instead of each wiring up bootstrap keys,
 * coming-soon keys, and the strip call themselves.
 */
export function useSanitizeExportSetting() {
  const { data: bootstrap } = useBootstrapApi();
  const { peopleOptions } = useExportOptions();

  // Valid field keys the backend still accepts, derived from the live bootstrap
  // response (strips stale columns such as FOLLOWER_COUNT from old templates).
  const validExportFieldKeys = useMemo(
    () => getValidExportFieldKeys(bootstrap?.export.titles),
    [bootstrap?.export.titles]
  );

  // Coming-soon fields still exist in bootstrap, so they aren't caught by the
  // valid-keys check; strip them separately so they never end up as columns in
  // the exported file. Coming-soon is resolved on the converted categories.
  const comingSoonFieldKeys = useMemo(
    () => getComingSoonFieldKeys(peopleOptions),
    [peopleOptions]
  );

  return useCallback(
    (setting: TransformedData): TransformedData =>
      stripUnexportableFields(
        setting,
        validExportFieldKeys,
        comingSoonFieldKeys
      ),
    [validExportFieldKeys, comingSoonFieldKeys]
  );
}
